import { useState, useMemo } from 'react';
import { Search, Users, MessageCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@iheart/ui';
import { api } from '../../lib/api';
import { useCurrentWorkspace } from '../../lib/workspace';
import { useAuth } from '../../lib/auth';
import { useStartDm, useCreateGroup } from '../../lib/chat';
import { useChatWidgetStore } from './ChatWidgetStore';

interface WorkspaceMember {
  publicId: string;
  name: string;
  email: string;
  role: string;
}

type Tab = 'dm' | 'group';

export function WidgetNewChat() {
  const ws = useCurrentWorkspace();
  const { user } = useAuth();
  const store = useChatWidgetStore();
  const startDm = useStartDm();
  const createGroup = useCreateGroup();

  const [tab, setTab] = useState<Tab>('dm');
  const [search, setSearch] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());

  const members = useQuery({
    queryKey: ['workspace-members', ws?.publicId],
    queryFn: () =>
      api<{ data: WorkspaceMember[] }>(
        `/workspaces/${ws!.publicId}/members`,
      ).then((r) => r.data),
    enabled: !!ws,
  });

  // Filter out current user and apply search
  const filtered = useMemo(() => {
    if (!members.data) return [];
    return members.data
      .filter((m) => m.publicId !== user?.publicId)
      .filter(
        (m) =>
          !search ||
          m.name.toLowerCase().includes(search.toLowerCase()) ||
          m.email.toLowerCase().includes(search.toLowerCase()),
      );
  }, [members.data, user?.publicId, search]);

  async function handleStartDm(memberPublicId: string) {
    try {
      const result = await startDm.mutateAsync(memberPublicId);
      store.goToThread(result.publicId);
    } catch { /* ignore */ }
  }

  async function handleCreateGroup() {
    if (!groupName.trim() || selectedMembers.size === 0) return;
    try {
      const result = await createGroup.mutateAsync({
        name: groupName.trim(),
        memberPublicIds: [...selectedMembers],
      });
      store.goToThread(result.publicId);
    } catch { /* ignore */ }
  }

  function toggleMember(publicId: string) {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Tab toggle */}
      <div className="flex border-b border-neutral-100">
        <button
          type="button"
          onClick={() => setTab('dm')}
          className={cn(
            'flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors',
            tab === 'dm'
              ? 'border-b-2 border-pink-500 text-pink-600'
              : 'text-neutral-400 hover:text-neutral-600',
          )}
        >
          <MessageCircle className="size-3.5" /> Direct Message
        </button>
        <button
          type="button"
          onClick={() => setTab('group')}
          className={cn(
            'flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors',
            tab === 'group'
              ? 'border-b-2 border-pink-500 text-pink-600'
              : 'text-neutral-400 hover:text-neutral-600',
          )}
        >
          <Users className="size-3.5" /> Group Chat
        </button>
      </div>

      {/* Group name input */}
      {tab === 'group' && (
        <div className="border-b border-neutral-100 px-3 py-2">
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Group name..."
            className="w-full rounded-md border border-neutral-200 px-2.5 py-1.5 text-sm outline-none focus:border-pink-300"
          />
        </div>
      )}

      {/* Search */}
      <div className="border-b border-neutral-100 px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search team members..."
            className="w-full rounded-md border border-neutral-200 py-1.5 pl-8 pr-3 text-sm outline-none focus:border-pink-300"
          />
        </div>
      </div>

      {/* Member list */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {!members.data ? (
          <div className="flex items-center justify-center py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-pink-200 border-t-pink-500" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-xs text-neutral-400">No team members found</p>
        ) : (
          <div className="space-y-0.5">
            {filtered.map((m) => (
              <button
                key={m.publicId}
                type="button"
                onClick={() => (tab === 'dm' ? handleStartDm(m.publicId) : toggleMember(m.publicId))}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-neutral-50',
                  tab === 'group' && selectedMembers.has(m.publicId) && 'bg-pink-50 ring-1 ring-pink-200',
                )}
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[11px] font-bold text-neutral-600">
                  {m.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-neutral-700">{m.name}</div>
                  <div className="truncate text-[11px] text-neutral-400">{m.role}</div>
                </div>
                {tab === 'group' && (
                  <span
                    className={cn(
                      'size-4 rounded border transition-colors',
                      selectedMembers.has(m.publicId)
                        ? 'border-pink-500 bg-pink-500'
                        : 'border-neutral-300',
                    )}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Group create button */}
      {tab === 'group' && selectedMembers.size > 0 && (
        <div className="border-t border-neutral-100 p-3">
          <button
            type="button"
            onClick={handleCreateGroup}
            disabled={!groupName.trim() || createGroup.isPending}
            className="w-full rounded-lg bg-pink-500 py-2 text-sm font-medium text-white transition-colors hover:bg-pink-600 disabled:opacity-50"
          >
            {createGroup.isPending ? 'Creating...' : `Create group (${selectedMembers.size} members)`}
          </button>
        </div>
      )}
    </div>
  );
}
